import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, TextField, Autocomplete, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { getExercises, saveWorkoutProgram, addProgram, getTraineesByTrainerId, getImage } from '../../utils/api'; import { GoogleGenerativeAI } from "@google/generative-ai";
import Loader from '../../client/Loader';
import ProgramExerciseItem from './ProgramExerciseItem';
import jsPDF from 'jspdf';
import GveretLevinFont from '../../assets/gevlevin-normal.js'; // Adjust the path as needed
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'; // Coin icon

const Workout = ({ userId }) => {
    const [exercises, setExercises] = useState([]);
    const [workoutType, setWorkoutType] = useState('');
    const [expertiseLevel, setExpertiseLevel] = useState('');
    const [preDefinedProgram, setPreDefinedProgram] = useState({});
    const [customProgram, setCustomProgram] = useState({});
    const [timesPerWeek, setTimesPerWeek] = useState(null);
    const [restDays, setRestDays] = useState('');
    const [generalNotes, setGeneralNotes] = useState([]);
    const [selectedProgramType, setSelectedProgramType] = useState('prebuilt'); // New state for program type
    const [isGenerating, setIsGenerating] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [sets, setSets] = useState('');
    const [reps, setReps] = useState('');
    const [weight, setWeight] = useState('');
    const [day, setDay] = useState('');
    const dayOptions = ['אימון 1', 'אימון 2', 'אימון 3', 'אימון 4', 'אימון 5', 'אימון 6', 'אימון 7'];
    const [showPopup, setShowPopup] = useState(false);//popup
    const [isTraineeModalOpen, setIsTraineeModalOpen] = useState(false);
    const [selectedTrainee, setSelectedTrainee] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [trainees, setTrainees] = useState([]);

    const exerciseNameMapping = {
        "Cat stretch": "מתיחת חתול",
        "Dumbbell Bicep Curl:": "כפיפת מרפקים עם משקולת",
        "Dumbbell Hammer Curl": "כפיפת מרפקים פטיש עם משקולת",
        "Prone abdominal stretch": "מתיחת בטן בשכיבה על הבטן",
        "Chest and biceps stretch": "מתיחת חזה ושרירי יד קדמיים",
        "Standing quadriceps stretch": "מתיחת ארבע ראשי בעמידה", // Changed from "רגל קדמית" to be more specific
        "Lying glute stretch": "מתיחת ישבן בשכיבה",
        "Bent-over barbell row": "חתירה עם מוט בעמידה כפופה",
        "Twisting hip extension": "הארכת ירך עם סיבוב",
        "Smith machine bent-knee good morning": "בוקר טוב עם מכונת סמית' וברכיים כפופות",
        "Machine back extension": "הארכת גב במכונה",
        "Good morning": "בוקר טוב",
        "Bird dog": "ציפור-כלב",
        "Superman": "סופרמן",
        "Assisted Chin Tuck": "טאק סנטר בסיוע",
        "Weighted Lying Neck Flexion (with head harness)": "כפיפת צוואר משוקללת בשכיבה (עם רתמה)",
        "Weighted Lying Neck Extension (with head harness)": "הארכת צוואר משוקללת בשכיבה (עם רתמה)",
        "Stability ball wall squat": "סקווט קיר עם כדור יציבות",
        "Bodyweight squat": "סקווט עם משקל גוף",
        "Dumbbell deadlift": "דדליפט עם משקולות יד",
        "Zercher squat": "סקווט זרשר",
        "Barbell front box squat": "סקווט קדמי לקופסה עם מוט", // Adjusted "על קופסה" to "לקופסה" for clarity
        "Barbell front squat": "סקווט קדמי עם מוט",
        "Barbell Bulgarian split squat": "סקווט מפוצל בולגרי עם מוט",
        "Dumbbell one-leg split squat": "סקווט מפוצל חד-רגלי עם משקולת",
        "Barbell side lunge": "לאנג' צידי עם מוט",
        "Cable hip abduction": "הרחקת ירך עם כבל",
        "Raised knee walking": "הליכה עם הרמת ברכיים",
        "Lateral leg raises": "הרמות רגל צידיות",
        "Standing Hip Abduction": "הרחקת ירך בעמידה",
        "Trap bar deadlift": "דדליפט עם מוט טרפז",
        "Dumbbell step-up": "עליית מדרגה עם משקולת",
        "Barbell one-leg hip thrust": "דחיפת ירך חד-רגלית עם מוט",
        "Barbell hip thrust": "דחיפת ירך עם מוט",
        "Weighted one-leg hip thrust": "דחיפת ירך חד-רגלית משוקללת",
        "Dirty Dog": "כלב מלוכלך",
        "Side Lying Clam Hips": "צדפה בשכיבה צידית", // Removed "לירכיים" as it’s redundant
        "Lunge": "לאנג'",
        "kneeling one arm pulldown": "משיכה חד-זרועית בברכיים",
        "Dumbbell Bench Squat": "סקווט עם משקולת ליד ספסל", // Clarified as a squat near a bench
        "Kneeling hip flexor stretch": "מתיחת כופפי ירך בברכיים", // Changed "מפשעה" to specify hip flexors
        "Side Lunge Stretch": "מתיחת לאנג' צידי",
        "Machine seated hip abduction": "הרחקת ירך בישיבה במכונה",
        "Single leg glute bridge": "גשר ישבן חד-רגלי",
        "Reverse Lunge": "לאנג' אחורי",
        "Barbell lunge": "לאנג' עם מוט",
        "Deep Squat": "סקווט עמוק", // Corrected from "סומו עמוק" to distinguish from Sumo Squat
        "bent over hip extension": "הארכת ירך כפופה",
        "Dumbbell lunge": "לאנג' עם משקולת",
        "Dumbbell forward-leaning lunge": "לאנג' עם נטייה קדימה ומשקולת", // Adjusted for better phrasing
        "Barbell deadlift": "דדליפט עם מוט",
        "Stability ball V-pass": "מעבר V עם כדור יציבות",
        "Bent-knee oblique v-up": "V-up אלכסוני עם ברכיים כפופות",
        "Oblique Crunches – On The Floor": "קראנצ'ים אלכסוניים על הרצפה",
        "Dead Bug": "חיפושית מתה",
        "Swiss Ball Rollout": "גלגול כדור שווייצרי",
        "Vertical leg crunch": "קראנץ' רגליים אנכי",
        "Twisting crunch": "קראנץ' מסתובב",
        "Hanging leg raise": "הרמת רגליים בתלייה",
        "Crunch with stability ball leg raise": "קראנץ' עם הרמת רגליים וכדור יציבות",
        "Weighted stability ball side bend": "כיפוף צידי משוקלל עם כדור יציבות",
        "V-up": "V-up",
        "Crunch (hands overhead)": "קראנץ' (ידיים מעל הראש)",
        "Kneeling cable crunch": "קראנץ' כבלים בברכיים",
        "Crunch (arms straight)": "קראנץ' (זרועות ישרות)",
        "Crunch Floor": "קראנץ' על הרצפה", // Simplified from "רצפה"
        "Frog crunch": "קראנץ' צפרדע",
        "Decline sit-up": "סיט-אפ בירידה",
        "Crunch (leg raise)": "קראנץ' (הרמת רגליים)",
        "Bicycle crunch": "קראנץ' אופניים",
        "Pistol Squat to Box": "סקווט פיסטול לקופסה",
        "Alternate Lying Floor Leg Raise": "הרמת רגליים לסירוגין בשכיבה על הרצפה",
        "Stability ball jackknife": "ג'קנייף עם כדור יציבות",
        "Sit-up": "סיט-אפ",
        "Cable one-arm reverse-grip triceps push-down": "דחיפת טריספס חד-זרועית עם אחיזה הפוכה וכבל",
        "Seated dumbbell overhead triceps extension": "הארכת טריספס מעל הראש בישיבה עם משקולת",
        "Seated bent-over two-arm dumbbell kickback": "קיקבק דו-זרועי בישיבה כפופה עם משקולות",
        "Triceps dip": "דיפ טריספס",
        "Reverse Grip Triceps Pushdown": "דחיפת טריספס עם אחיזה הפוכה",
        "Bent-knee bench dip": "דיפ ספסל עם ברכיים כפופות",
        "Triceps rope push-down": "דחיפת טריספס עם חבל",
        "One-arm dumbbell preacher curl": "כפיפת מרפקים פריצ'ר חד-זרועי עם משקולת",
        "Dumbbell one-arm reverse wrist curl": "כפיפת מרפקים שורש כף יד הפוך חד-זרועי עם משקולת",
        "Behind-the-back barbell wrist curl": "כפיפת מרפקים שורש כף יד מאחורי הגב עם מוט",
        "Cable curl": "כפיפת מרפקים עם כבל",
        "Seated barbell wrist curl": "כפיפת מרפקים שורש כף יד בישיבה עם מוט",
        "Dumbbell curl": "כפיפת מרפקים עם משקולת",
        "Dumbbell reverse curl": "כפיפת מרפקים הפוך עם משקולת",
        "Barbell reverse curl": "כפיפת מרפקים הפוך עם מוט",
        "EZ bar reverse curl": "כפיפת מרפקים הפוך עם מוט EZ",
        "Barbell skull crusher": "סקאל קראשר עם מוט",
        "Barbell JM press": "לחיצת JM עם מוט", // Corrected word order
        "Close-grip EZ bar curl": "כפיפת מרפקים צמוד עם מוט EZ",
        "Overhead EZ bar triceps extension": "הארכת טריספס מעל הראש עם מוט EZ",
        "Swiss Ball Lying Triceps Extension": "הארכת טריספס בשכיבה על כדור שווייצרי",
        "EZ bar curl": "כפיפת מרפקים עם מוט EZ",
        "Dumbbell hammer preacher curl": "כפיפת מרפקים פטיש פריצ'ר עם משקולת",
        "Tate press": "לחיצת טייט",
        "Cable triceps kickback": "קיקבק טריספס עם כבל",
        "Close-grip push-up": "שכיבות סמיכה צמודות",
        "Dumbbell kickback": "קיקבק עם משקולת",
        "Dumbbell One-Arm Triceps Extension Instructions": "הארכת טריספס חד-זרועית עם משקולת",
        "Dumbbell Seated Revers grip Concentration Curl": "כפיפת מרפקים ריכוז בישיבה עם אחיזה הפוכה ומשקולת",
        "Dumbbell Kickbacks on Exercise Ball": "קיקבק עם משקולת על כדור אימון",
        "Freedom Band Pushdown": "דחיפה עם רצועת חופש",
        "Glute Bridge on floor": "גשר ישבן על הרצפה",
        "Prone incline wide-grip upright row": "חתירה אנכית רחבה בשכיבה על ספסל משופע",
        "Barbell Clean and Press": "קלין ולחיצה עם מוט", // Adjusted "אנד" to "ו"
        "Barbell shrug": "שרג עם מוט",
        "Bent-over dumbbell row": "חתירה כפופה עם משקולות",
        "One-leg hyperextension": "הארכת גב חד-רגלית",
        "Standing cable rear delt row with rope": "חתירת דלתא אחורית בעמידה עם חבל",
        "wide front grip lat pull downs": "משיכות לאט רחבות עם אחיזה קדמית",
        "two arm dumbbell rows": "חתירה דו-זרועית עם משקולות",
        "Keiser twist (down up)": "טוויסט קייזר (למטה-למעלה)",
        "Freedom Pull Apart": "משיכה חופשית",
        "Capol over rear lateral raise": "הרמה צידית אחורית מעל הראש", // Assuming "Capol" is a typo or brand
        "Hyperextension Bench Exercise": "תרגיל הארכת גב על ספסל",
        "Pull-up": "מתח",
        "Bent-over two-arm dumbbell row": "חתירה כפופה דו-זרועית עם משקולות",
        "Underhand Yates row": "חתירת ייטס עם אחיזה תחתית",
        "Ankle – Dorsal Flexion": "פשיטת קרסול",
        "Machine seated calf raise": "הרמת עגלים בישיבה במכונה",
        "Mountain Climbers": "מטפסי הרים",
        "High bird dog plank": "פלאנק ציפור-כלב גבוה",
        "Band twist (up down)": "טוויסט עם רצועה (למעלה-למטה)",
        "Stability ball decline push-up": "שכיבות סמיכה בירידה עם כדור יציבות",
        "One-leg push-up": "שכיבות סמיכה חד-רגלית",
        "Incline dumbbell fly": "פלי משקולות בשיפוע",
        "Isometric wiper": "וויפר איזומטרי",
        "Front Dumbbell Raise": "הרמת משקולות קדמית",
        "Machine fly": "פלי במכונה",
        "Dumbbell fly": "פלי עם משקולות",
        "Decline dumbbell bench press": "לחיצת חזה בירידה עם משקולות",
        "Dumbbell bench press": "לחיצת חזה עם משקולות",
        "Cable crossovers": "קרוסאובר עם כבלים",
        "high fly": "פלי גבוה",
        "Incline barbell bench press": "לחיצת חזה בשיפוע עם מוט",
        "Decline barbell bench press": "לחיצת חזה בירידה עם מוט",
        "Push up (on knees)": "שכיבות סמיכה (על הברכיים)",
        "Push up (bosu ball)": "שכיבות סמיכה (על כדור בוסו)",
        "Push-ups": "שכיבות סמיכה",
        "Kettlebell swing": "נפת קטלבל",
        "Decline dumbbell fly": "פלי בירידה עם משקולות",
        "Weighted push-up": "שכיבות סמיכה משוקללות",
        "Stability ball push-up": "שכיבות סמיכה עם כדור יציבות",
        "Seated dumbbell overhead press": "לחיצת כתפיים מעל הראש בישיבה עם משקולות",
        "Behind-the-neck barbell overhead press": "לחיצת כתפיים מעל הראש מאחורי הצוואר עם מוט",
        "Smith machine shrug": "שרג במכונת סמית'",
        "Dumbbell w-press": "לחיצת W עם משקולות",
        "Cable front raise": "הרמה קדמית עם כבל",
        "Cable one-arm front raise": "הרמה קדמית חד-זרועית עם כבל",
        "Dumbbell Arnold Press": "לחיצת ארנולד עם משקולות",
        "Weighted Front Raise": "הרמה קדמית משוקללת",
        "Seated alternating dumbbell front raise": "הרמה קדמית לסירוגין בישיבה עם משקולות",
        "Barbell wide-grip upright row": "חתירה אנכית רחבה עם מוט",
        "One-arm dumbbell bent-over lateral raise": "הרמה צידית חד-זרועית כפופה עם משקולת",
        "Alternating dumbbell front raise": "הרמה קדמית לסירוגין עם משקולות",
        "Seated dumbbell front raise": "הרמה קדמית בישיבה עם משקולות",
        "Dumbbell lateral raise": "הרמה צידית עם משקולות",
        "Deltoid Raise": "הרמת דלתואיד",
        "Shoulder Press": "לחיצת כתפיים",
        "Double cable front raise": "הרמה קדמית כפולה עם כבלים",
        "Barbell Upright Row": "חתירה אנכית עם מוט",
        "Lying dumbbell one-arm rear lateral raise": "הרמה צידית אחורית חד-זרועית בשכיבה עם משקולת",
        "Πιέσεις με αλτήρες για ώμους – όρθια στάση": "לחיצת כתפיים עם משקולות בעמידה",
        "Dumbbell one-arm overhead press": "לחיצת כתפיים חד-זרועית מעל הראש עם משקולת",
        "Seated barbell overhead press": "לחיצת כתפיים מעל הראש בישיבה עם מוט",
        "Dumbbell Cuban rotation": "רוטציה קובנית עם משקולת",
        "Εμπρόσθιες άρσεις ώμων με μπάρα": "הרמות קדמיות לכתפיים עם מוט",
        "Lying dumbbell rear lateral raise": "הרמה צידית אחורית בשכיבה עם משקולת",
        "Έξω στροφή ώμου με τον ώμο σε 90 μοίρες απαγωγή": "סיבוב חיצוני של הכתף ב-90 מעלות אבדוקציה",
        "Cable external shoulder rotation": "סיבוב חיצוני של הכתף עם כבל",
        "Dumbbell Scott press": "לחיצת סקוט עם משקולת",
        "Άρσεις τραπεζοειδών με μπάρα": "הרמות טרפז עם מוט",
        "Dumbbell bent-over lateral raise": "הרמה צידית כפופה עם משקולת",
        "Seated bent-over lateral raise": "הרמה צידית כפופה בישיבה",
        "Barbell overhead press": "לחיצת כתפיים מעל הראש עם מוט",
        "Barbell Romanian Deadlift": "דדליפט רומני עם מוט",
        "Kettlebell Goblet Squat": "סקווט גביע עם קטלבל",
        "Standing Calf Raise": "הרמת עגלים בעמידה",
        "Seated Calf Raise": "הרמת עגלים בישיבה",
        "Dumbbell Romanian Deadlift": "דדליפט רומני עם משקולות",
        "Side Plank": "פלאנק צידי",
        "Plank": "פלאנק",
        "Russian Twist": "טוויסט רוסי",
        "Weighted Plank": "פלאנק משוקלל",
        "Cable Woodchopper": "חוטב עצים עם כבל",
        "Leg Press": "לחיצת רגליים",
        "Hack Squat": "סקווט האק",
        "Dumbbell Goblet Squat": "סקווט גביע עם משקולת",
        "Box Jump": "קפיצה לקופסה",
        "Step-Up": "עליית מדרגה",
        "Farmer’s Walk": "הליכת חוואי",
        "Wall Sit": "ישיבה על הקיר",
        "Reverse Hyper": "היפר הפוך",
        "Glute Kickback": "קיקבק ישבן",
        "Donkey Calf Raise": "הרמת עגלים דונקי",
        "Seated Leg Curl": "כפיפת רגליים בישיבה",
        "Lying Leg Curl": "כפיפת רגליים בשכיבה",
        "Nordic Hamstring Curl": "כפיפת רגליים נורדית",
        "Barbell Bench Press": "לחיצת חזה עם מוט",
        "Incline Push-Up": "שכיבות סמיכה בשיפוע",
        "Dumbbell Pullover": "פולאובר עם משקולת",
        "Chest Press Machine": "מכונת לחיצת חזה",
        "Pec Deck": "פק דק",
        "Barbell Incline Bench Press": "לחיצת חזה בשיפוע עם מוט",
        "Close-Grip Bench Press": "לחיצת חזה צמודה עם מוט",
        "Dumbbell Incline Press": "לחיצת חזה בשיפוע עם משקולות",
        "Lat Pulldown": "משיכות לאט",
        "Seated Row": "חתירה בישיבה",
        "T-Bar Row": "חתירת T-Bar",
        "Face Pull": "פייס פול",
        "Dumbbell Shrug": "שרג עם משקולות",
        "Barbell Curl": "כפיפת מרפקים עם מוט",
        "Hammer Curl": "כפיפת מרפקים פטיש",
        "Concentration Curl": "כפיפת מרפקים ריכוז",
        "Preacher Curl": "כפיפת מרפקים פריצ'ר",
        "Triceps Pushdown": "דחיפת טריספס",
        "Overhead Dumbbell Triceps Extension": "הארכת טריספס מעל הראש עם משקולת",
        "Dips": "דיפס",
        "Chin-Up": "מתח עם אחיזה תחתית",
        "Barbell Shoulder Press": "לחיצת כתפיים עם מוט",
        "Side Lateral Raise": "הרמה צידית",
        "Rear Delt Fly": "פלי דלתא אחורי",
        "Upright Row": "חתירה אנכית",
        "Assisted Chin up": "מתח בסיוע",
        "Barbell Clean Deadlift": "דדליפט בסגנון קלין עם מוט", // Adjusted for clarity
        "Barbell JM Press": "לחיצת JM עם מוט",
        "Bent-Over Barbell Reverse Raise (Shoulders)": "הרמה צידית אחורית כפופה עם מוט", // Specified as rear lateral raise
        "Bent-Over Two-Arm Dumbbell Row": "חתירה כפופה דו-זרועית עם משקולות",
        "Cable External Shoulder Rotation": "סיבוב חיצוני של הכתף עם כבל",
        "Cable Standing Rear Delt Horizontal Row": "חתירת דלתא אחורית אופקית עם כבל בעמידה",
        "Dumbbell Cuban Rotation": "רוטציה קובנית עם משקולת",
        "Dumbbell Scott Press": "לחיצת סקוט עם משקולת",
        "Dumbbell Seated Reverse Grip Concentration Curl": "כפיפת מרפקים ריכוז בישיבה עם אחיזה הפוכה ומשקולת",
        "Gripless shrug": "שרג ללא אחיזה",
        "High Bird Dog Plank": "פלאנק ציפור-כלב גבוה",
        "Kettlebell Kneeling One Arm Bottoms Up Press": "לחיצת חד-זרועית עם קטלבל בברכיים (תחתית למעלה)",
        "Lever Bent over Row (plate loaded)": "חתירה כפופה עם מכונה (טעינת משקולות)",
        "Lying Dumbbell One-Arm Rear Lateral Raise": "הרמה צידית אחורית חד-זרועית בשכיבה עם משקולת",
        "Lying Dumbbell Rear Lateral Raise": "הרמה צידית אחורית בשכיבה עם משקולת",
        "One-Arm Dumbbell Bent-Over Lateral Raise": "הרמה צידית חד-זרועית כפופה עם משקולת",
        "Seated Alternating Dumbbell Front Raise": "הרמה קדמית לסירוגין בישיבה עם משקולות",
        "Seated Bent-Over Lateral Raise": "הרמה צידית כפופה בישיבה",
        "Spider Crawl Push up": "שכיבות סמיכה זחילה עכבישית",
        "Standing Long Jump (female)": "קפיצה לרוחק בעמידה (נשים)",
        "Underhand Yates Row": "חתירת ייטס עם אחיזה תחתית",
        "Sumo Squat": "סקווט סומו", // Corrected from "סומו עמוק" to distinguish from Deep Squat
        "45-Degree Leg Press": "לחיצת רגליים 45 מעלות",
        "Lying Hip Thrusts with Bent Knees": "דחיפת ירך בשכיבה עם ברכיים כפופות", // Aligned with "hip thrust" terminology
        "Standing Triceps Extensions with Barbell": "הארכת טריספס בעמידה עם מוט",
        "Close-Grip Lat Pulldowns": "משיכות לאט עם אחיזה צמודה", // Corrected typo "תרווחליה"
        "Bent-Over Barbell Row": "חתירה כפופה עם מוט",
        "Trap Raises with Cable": "הרמות טרפז עם כבל", // Corrected "תרווחליה" to "כבל"
        "Dumbbell Shoulder Press – Standing": "לחיצת כתפיים עם משקולות בעמידה",
        "Front Barbell Shoulder Raises": "הרמות קדמיות לכתפיים עם מוט",
        "External Shoulder Rotation with Shoulder at 90 Degrees Abduction": "סיבוב חיצוני של הכתף ב-90 מעלות אבדוקציה",
        "Barbell Trap Raises": "הרמות טרפז עם מוט"
    };

    const hebrewToEnglishMapping = Object.fromEntries(
        Object.entries(exerciseNameMapping).map(([english, hebrew]) => [hebrew, english])
    );

    useEffect(() => {
        const fetchExercises = async () => {
            try {
                const response = await getExercises();
                setExercises(response);
                const ids = response.map(ex => ex._id);
                const uniqueIds = new Set(ids);
                if (uniqueIds.size !== ids.length) {
                    console.warn('Duplicate _id values found in exercises:', ids);
                }
            } catch (error) {
                console.error('שגיאה בטעינת התרגילים:', error);
            }
        };
        fetchExercises();
    }, []);

    const generateProgram = async () => {
        if (!workoutType || !expertiseLevel) {
            return;
        }
        setIsGenerating(true);
        try {
            const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
            const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });
            const exerciseNames = exercises.map(ex => ex.name).join(', ');
            const prompt = `צור בעברית תוכנית אימונים מסוג ${workoutType} עבור מתאמן ברמת ${expertiseLevel} תוך שימוש רק בתרגילים הבאים: ${exerciseNames}. 
                מבנה התוכנית יהיה:
                - 'קודם כל 'האימונים בשבוע : 
                - ימי המנוחה' רק במספרים'
                -  'דגשים כללים' 
                - ואז אימונים שכותרתם "אימון 1", "אימון 2", "אימון.." וכן הלאה במידת הצורך .
                
                עבור כל אימון, רשום את התרגילים בפורמט הבא: "* **שם התרגיל:** X סטים של Y חזרות".
                חשוב לא לכלול טקסט נוסף, הסברים או שינויים מעבר למבנה המצוין. שים לב שסכום האימונים וימי המנוחה אמור להיות 7 שזה שבוע`;

            const result = await model.generateContent(prompt);
            const text = result.response.text();

            const programLines = text.split('\n').filter(line => line.trim() !== '');
            let timesPerWeek = null;
            let restDays = '';
            let generalNotes = [];
            let currentDay = null;
            const programByDay = {};

            for (const line of programLines) {
                const trimmedLine = line.trim();
                if (trimmedLine.includes('האימונים בשבוע:')) {
                    const match = trimmedLine.match(/האימונים בשבוע:\s*(\d+)/);
                    if (match) {
                        timesPerWeek = parseInt(match[1]);
                    }
                } else if (trimmedLine.includes('אימון')) {
                    currentDay = trimmedLine.replace(/\*/g, '').trim();
                    programByDay[currentDay] = [];
                } else if (currentDay && trimmedLine.startsWith("* **")) {
                    const exerciseMatch = trimmedLine.match(/\*\s*\*\*(.+?):\*\*\s*(\d+)\s*סטים של\s*(\d+)-?(\d+)?\s*חזרות/);
                    if (exerciseMatch) {
                        let exerciseName = exerciseMatch[1].trim().replace(/:$/, '');
                        const sets = parseInt(exerciseMatch[2]);
                        const repMin = parseInt(exerciseMatch[3]);
                        const repMax = exerciseMatch[4] ? parseInt(exerciseMatch[4]) : repMin;
                        const reps = Math.round((repMin + repMax) / 2);
                        const englishName = hebrewToEnglishMapping[exerciseName] || exerciseName;
                        const exercise = exercises.find(ex => ex.name.toLowerCase() === englishName.toLowerCase());
                        if (exercise) {
                            programByDay[currentDay].push({ exercise, sets, reps });
                        } else {
                            console.warn(`תרגיל לא נמצא: ${exerciseName}`);
                        }
                    }
                } else if (trimmedLine.includes('ימי המנוחה:')) {
                    restDays = trimmedLine.replace('ימי המנוחה:', '').trim();
                } else if (trimmedLine.includes('דגשים כללים:')) {
                    generalNotes.push(trimmedLine.replace('דגשים כללים:', '').trim());
                } else if (generalNotes.length > 0) {
                    generalNotes.push(trimmedLine);
                }
            }

            setTimesPerWeek(timesPerWeek);
            setPreDefinedProgram(programByDay);
            setRestDays(restDays);
            setGeneralNotes(generalNotes);
        } catch (error) {
            console.error('שגיאה ביצירת התוכנית:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleAddExercise = () => {
        if (!selectedExercise || !day || isNaN(parseInt(sets)) || isNaN(parseInt(reps))) {
            return;
        }
        const dayKey = day;
        const newExercise = {
            exercise: selectedExercise,
            sets: parseInt(sets),
            reps: parseInt(reps),
            weight: weight ? parseFloat(weight) : undefined
        };
        setCustomProgram(prev => {
            const updated = { ...prev };
            if (!updated[dayKey]) {
                updated[dayKey] = [];
            }
            updated[dayKey].push(newExercise);
            return updated;
        });
        setOpenAddDialog(false);
        setSelectedExercise(null);
        setSets('');
        setReps('');
        setWeight('');
        setDay('');
    };

    const handleRemoveExercise = (day, index) => {
        setCustomProgram(prev => {
            const updated = { ...prev };
            updated[day] = updated[day].filter((_, i) => i !== index);
            if (updated[day].length === 0) {
                delete updated[day];
            }
            return updated;
        });
    };

    const handleEditSetsRepsWeight = (day, index, newSets, newReps, newWeight) => {
        setCustomProgram(prev => {
            const updated = { ...prev };
            updated[day][index] = {
                ...updated[day][index],
                sets: newSets,
                reps: newReps,
                weight: newWeight
            };
            return updated;
        });
    };
    // Utility function to convert array to base64
    const arrayToBase64 = (array) => {
        const uint8Array = new Uint8Array(array);
        const binaryString = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
        return btoa(binaryString);
    };

    // Fetch exercises and trainees on component mount
    useEffect(() => {
        const fetchExercises = async () => {
            try {
                const response = await getExercises();
                setExercises(response);
            } catch (error) {
                console.error('שגיאה בטעינת התרגילים:', error);
            }
        };

        const fetchTrainees = async () => {
            try {
                const traineesList = await getTraineesByTrainerId(userId);
                const traineesWithImages = await Promise.all(traineesList.map(async (trainee) => {
                    try {
                        const profileImageBase = await getImage(trainee._id);
                        const profileImageBase64 = profileImageBase?.image;
                        return {
                            ...trainee,
                            profileImageBase64
                        };
                    } catch (error) {
                        console.error(`שגיאה בטעינת תמונה עבור מתאמן ${trainee._id}:`, error);
                        return {
                            ...trainee,
                            profileImageBase64: '/default-avatar.png'
                        };
                    }
                }));
                setTrainees(traineesWithImages);
            } catch (e) {
                console.error('שגיאה בטעינת המתאמנים:', e);
            }
        };
        fetchExercises();
        fetchTrainees();
    }, [userId]);
    
    const handleConfirmSave = async () => {
        try {
            const program = selectedProgramType === 'prebuilt' ? preDefinedProgram : customProgram;
            const isCustom = selectedProgramType === 'custom';

            if (Object.keys(program).length === 0) {
                alert('אנא צור תוכנית מוכנה או הוסף תרגילים לתוכנית מותאמת אישית לפני השמירה.');
                return;
            }

            const programData = {
                userId: selectedTrainee._id,
                workoutType,
                expertiseLevel,
                timesPerWeek,
                restDays,
                generalNotes,
                exercises: isCustom
                    ? Object.entries(customProgram).flatMap(([day, exercises]) =>
                        exercises.map(ex => ({
                            day,
                            exerciseId: ex.exercise._id,
                            exerciseName: ex.exercise.name,
                            sets: ex.sets,
                            reps: ex.reps,
                            weight: ex.weight,
                        }))
                    )
                    : Object.entries(program).flatMap(([day, exercises]) =>
                        exercises.map(ex => ({
                            day,
                            exerciseId: ex.exercise._id,
                            exerciseName: ex.exercise.name,
                            sets: ex.sets,
                            reps: ex.reps,
                        }))
                    ),
            };

            const savedProgram = await saveWorkoutProgram(programData);
            const workoutProgramId = savedProgram.program._id;
            const pdfBlob = generatePDF(programData, selectedTrainee);
            const formData = new FormData();
            formData.append('programFile', pdfBlob, `${workoutType || 'Custom'}_Program.pdf`);
            formData.append('userId', selectedTrainee._id);
            formData.append('workoutProgramId', workoutProgramId);
            await addProgram(formData);

            setShowPopup(true);
        } catch (error) {
            console.error('שגיאה בשמירת התוכנית:', error);
        } finally {
            setIsConfirmModalOpen(false);
        }
    };

    const generatePDF = (programData, selectedTrainee) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            textDirection: 'rtl',
            direction: 'rtl',
        });
        doc.setR2L(true);
        doc.addFileToVFS('gevlevin.ttf', GveretLevinFont);
        doc.addFont('gevlevin.ttf', 'GveretLevin', 'normal');
        doc.setFont('GveretLevin');
        doc.setLanguage('he');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);

        // Define page dimensions and margins
        const pageWidth = 210;
        const rightMargin = 10;
        const leftMargin = 10; // Add a left margin
        const textX = pageWidth - rightMargin; // 200mm
        const indent = 5;

        // Helper function to add wrapped text
        const addWrappedText = (text, x, y, maxWidth) => {
            const lines = doc.splitTextToSize(text, maxWidth);
            doc.text(lines, x, y, { align: 'right' });
            return lines.length * 10; // Return height increment (10mm per line)
        };

        let y = 20;
        doc.text('תוכנית אימונים', 105, 10, { align: 'center' });

        // Add main program details with wrapping
        // y += addWrappedText(
        //     `סוג אימון: ${workoutType || 'מותאם אישית'}`,
        //     textX,
        //     y,
        //     textX - leftMargin // maxWidth = 200mm - 10mm = 190mm
        // );
        // y += addWrappedText(
        //     `רמת ניסיון: ${expertiseLevel || 'לא מוגדר'}`,
        //     textX,
        //     y,
        //     textX - leftMargin
        // );
        doc.text(`תוכנית אימונים עבור ${selectedTrainee.username}`, 105, 10, { align: 'center' });
        y += 10;

        if (programData.timesPerWeek) {
            y += addWrappedText(
                `מספר פעמים בשבוע: ${programData.timesPerWeek}`,
                textX,
                y,
                textX - leftMargin
            );
        }
        if (programData.restDays) {
            y += addWrappedText(
                `ימי מנוחה: ${programData.restDays}`,
                textX,
                y,
                textX - leftMargin
            );
        }

        // Organize and add exercises by day
        const exercisesByDay = {};
        programData.exercises.forEach(ex => {
            exercisesByDay[ex.day] = exercisesByDay[ex.day] || [];
            exercisesByDay[ex.day].push(ex);
        });

        for (const [day, exercises] of Object.entries(exercisesByDay)) {
            y += addWrappedText(day, textX, y, textX - leftMargin);
            exercises.forEach((ex, index) => {
                const name = exerciseNameMapping[ex.exerciseName] || ex.exerciseName;
                const weightText = ex.weight ? `, משקל: ${ex.weight} ק״ג` : '';
                const exerciseText = `${index + 1}. ${name}: ${ex.sets} סטים של ${ex.reps} חזרות${weightText}`;
                const exerciseX = textX - indent; // 195mm
                y += addWrappedText(
                    exerciseText,
                    exerciseX,
                    y,
                    exerciseX - leftMargin // maxWidth = 195mm - 10mm = 185mm
                );
            });
            y += 5; // Extra spacing between days
        }

        // Add general notes with wrapping
        if (programData.generalNotes.length > 0) {
            y += addWrappedText('דגשים כללים:', textX, y, textX - leftMargin);
            programData.generalNotes.forEach(note => {
                y += addWrappedText(
                    `- ${note}`,
                    textX - indent,
                    y,
                    (textX - indent) - leftMargin
                );
            });
        }

        return doc.output('blob');
    };

    return (
        <Box sx={{
            padding: '20px',
            direction: 'rtl',
            margin: '0 auto',
            textAlign: 'center',
            maxWidth: '400px',
        }}>
            <Stack spacing={2} direction="column" alignItems="center" justifyContent="center">
                <Box sx={{ width: '100%', textAlign: 'right' }}>
                    <Typography variant="h4" mb="20px" sx={{ color: '#8B4513', fontFamily: 'GveretLevin', fontSize: '2rem' }}>
                        תוכניות אימונים
                    </Typography>
                </Box>

                {/* Select Workout Type and Expertise Level */}
                <Stack direction="column" spacing={2} mb="20px" sx={{ width: '100%' }}>
                    <Select
                        value={workoutType}
                        onChange={(e) => setWorkoutType(e.target.value)}
                        displayEmpty
                        sx={{ direction: 'rtl', textAlign: 'right', minWidth: '150px' }}
                    >
                        <MenuItem value="" disabled>בחר סוג אימון</MenuItem>
                        <MenuItem value="powerlifting">פאוורליפטינג</MenuItem>
                        <MenuItem value="powerbuilding">פאוורבילדינג</MenuItem>
                        <MenuItem value="SBD">SBD powerlifting</MenuItem>
                        <MenuItem value="gym training classic A B C">אימון קלאסי A B C</MenuItem>
                        <MenuItem value="A B">A B</MenuItem>
                        <MenuItem value="נשים הדגשת ישבן">נשים הדגשת ישבן</MenuItem>
                    </Select>
                    <Select
                        value={expertiseLevel}
                        onChange={(e) => setExpertiseLevel(e.target.value)}
                        displayEmpty
                        sx={{ direction: 'rtl', textAlign: 'right', minWidth: '100px' }}
                    >
                        <MenuItem value="" disabled>בחר רמת ניסיון</MenuItem>
                        <MenuItem value="beginner">מתחיל</MenuItem>
                        <MenuItem value="intermediate">בינוני</MenuItem>
                        <MenuItem value="advanced">מתקדם</MenuItem>
                    </Select>
                </Stack>

                {/* Radio Buttons for Program Type */}
                <Stack direction="row" spacing={2} mb="20px" sx={{ width: '100%' }}>
                    {/* <Typography>בחר סוג תוכנית:</Typography> */}
                    <RadioGroup row value={selectedProgramType} onChange={(e) => {
                        setSelectedProgramType(e.target.value);
                        if (e.target.value === 'prebuilt') {
                            setCustomProgram({});
                            // Keep timesPerWeek, restDays, generalNotes for prebuilt generation
                        } else {
                            setPreDefinedProgram({});
                            setTimesPerWeek(null);
                            setRestDays('');
                            setGeneralNotes([]);
                        }
                    }}>
                        <FormControlLabel value="prebuilt" control={<Radio />} label="תוכנית מוכנה" />
                        <FormControlLabel value="custom" control={<Radio />} label="תוכנית מותאמת אישית" />
                    </RadioGroup>
                </Stack>

                {/* Prebuilt Program Section */}
                {selectedProgramType === 'prebuilt' && (
                    <Stack mb="50px" sx={{ width: '100%' }}>
                        {/* <Typography variant="h5" mb="10px" sx={{ color: '#8B4513', fontFamily: 'GveretLevin', textAlign: 'center' }}>
                            תוכנית מוכנה 
                        </Typography> */}
                        <Stack direction="row" spacing={2} mb="20px">
                            <Box sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex', width: '100%' }}>
                                <Button
                                    variant="contained"

                                    onClick={generateProgram}
                                    disabled={isGenerating}
                                    sx={{
                                        backgroundColor: "#B5A78A",
                                        color: "white",
                                        "&:hover": { backgroundColor: "#B5A78A" },
                                    }}
                                >
                                    {isGenerating ? 'מייצר...' : 'צור תוכנית'}
                                </Button>
                            </Box>
                        </Stack>
                        {isGenerating && <Loader />}
                        {Object.keys(preDefinedProgram).length > 0 && (
                            <Stack>
                                <Typography variant="h6" mb="10px" sx={{ color: '#8B4513', fontFamily: 'GveretLevin', textAlign: 'right' }}>
                                    תוכנית שנוצרה
                                </Typography>
                                {timesPerWeek && (
                                    <Typography variant="body1" sx={{ fontFamily: 'GveretLevin', textAlign: 'right', marginBottom: '10px' }}>
                                        מספר פעמים בשבוע: {timesPerWeek}
                                    </Typography>
                                )}
                                {restDays && (
                                    <Typography variant="body1" sx={{ fontFamily: 'GveretLevin', textAlign: 'right', marginBottom: '10px' }}>
                                        ימי מנוחה: {restDays}
                                    </Typography>
                                )}
                                {generalNotes.length > 0 && (
                                    <Stack>
                                        <Typography variant="h6" sx={{ color: '#8B4513', fontFamily: 'GveretLevin', textAlign: 'right' }}>דגשים כללים</Typography>
                                        {generalNotes.map((note, index) => (
                                            <Typography key={index} variant="body1" sx={{ fontFamily: 'GveretLevin', textAlign: 'right' }}>
                                                {note}
                                            </Typography>
                                        ))}
                                    </Stack>
                                )}
                                {Object.entries(preDefinedProgram).map(([day, exercises]) => (
                                    <Stack key={day} sx={{ mb: 2 }}>
                                        <Typography variant="h6" marginTop="10px" sx={{ color: "#C4A484", fontFamily: 'GveretLevin', textAlign: 'right' }}>{day}</Typography>
                                        {exercises.map((item, index) => (
                                            <ProgramExerciseItem
                                                key={index}
                                                exercise={item.exercise}
                                                sets={item.sets}
                                                reps={item.reps}
                                            />
                                        ))}
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                )}

                {/* Custom Program Section */}
                {selectedProgramType === 'custom' && (
                    <Stack sx={{ width: '100%' }}>
                        {/* <Typography variant="h5" mb="10px" sx={{ color: '#8B4513', fontFamily: 'GveretLevin', textAlign: 'center' }}>
                            תוכנית מותאמת אישית
                        </Typography> */}
                        <TextField
                            label="מספר פעמים בשבוע"
                            type="number"
                            value={timesPerWeek || ''}
                            onChange={(e) => setTimesPerWeek(e.target.value ? parseInt(e.target.value) : null)}
                            sx={{ direction: 'rtl', textAlign: 'right', mb: 2 }}
                        />
                        <TextField

                            label="ימי מנוחה"
                            value={restDays}
                            onChange={(e) => setRestDays(e.target.value)}
                            sx={{ direction: 'rtl', textAlign: 'right', mb: 2 }}
                        />
                        <TextField
                            label="דגשים כללים"
                            multiline
                            rows={4}
                            value={generalNotes.join('\n')}
                            onChange={(e) => setGeneralNotes(e.target.value.split('\n'))}
                            sx={{ direction: 'rtl', textAlign: 'right', mb: 2 }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => setOpenAddDialog(true)}
                            sx={{ backgroundColor: '#8B4513', color: 'white', mb: 2 }}
                        >
                            הוסף תרגיל
                        </Button>
                        <Stack mt="20px">
                            {Object.entries(customProgram).map(([day, exercises]) => (
                                <Stack key={day} sx={{ mb: 2 }}>
                                    <Typography variant="h6" sx={{ color: "#C4A484", fontFamily: 'GveretLevin', textAlign: 'right' }}>{day}</Typography>
                                    {exercises.map((item, index) => (
                                        <ProgramExerciseItem
                                            key={`${item.exercise._id}-${index}`}
                                            exercise={item.exercise}
                                            sets={item.sets}
                                            reps={item.reps}
                                            weight={item.weight}
                                            editable
                                            onRemove={() => handleRemoveExercise(day, index)}
                                            onEdit={(newSets, newReps, newWeight) => handleEditSetsRepsWeight(day, index, newSets, newReps, newWeight)}
                                        />
                                    ))}
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>
                )}

                {/* Add Exercise Dialog */}
                <Dialog open={openAddDialog} onClose={() => {
                    setOpenAddDialog(false);
                    setSelectedExercise(null);
                    setSets('');
                    setReps('');
                    setWeight('');
                    setDay('');
                }}>
                    <DialogTitle sx={{ textAlign: 'right' }}>הוסף תרגיל לתוכנית</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 2, direction: 'rtl' }}>
                            <Autocomplete
                                fullWidth
                                options={exercises}
                                getOptionLabel={(option) => exerciseNameMapping[option.name] || option.name}
                                value={selectedExercise}
                                onChange={(event, newValue) => setSelectedExercise(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="חפש תרגיל"
                                        placeholder="הקלד כדי לחפש..."
                                        sx={{ direction: 'rtl', textAlign: 'right' }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option._id}>
                                        {exerciseNameMapping[option.name] || option.name}
                                    </li>
                                )}
                            />
                            <Autocomplete
                                options={dayOptions}
                                value={day}
                                onChange={(event, newValue) => setDay(newValue || '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="יום"
                                        placeholder="בחר יום"
                                        sx={{ direction: 'rtl', textAlign: 'right' }}
                                    />
                                )}
                            />
                            <TextField
                                label="סטים"
                                type="number"
                                value={sets}
                                onChange={(e) => setSets(e.target.value)}
                                sx={{ direction: 'rtl', textAlign: 'right' }}
                            />
                            <TextField
                                label="חזרות"
                                type="number"
                                value={reps}
                                onChange={(e) => setReps(e.target.value)}
                                sx={{ direction: 'rtl', textAlign: 'right' }}
                            />
                            <TextField
                                label="משקל (ק״ג)"
                                type="number"
                                value={weight}
                                onChange={(e) => setWeight(e.target.value)}
                                sx={{ direction: 'rtl', textAlign: 'right' }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAddExercise}
                                sx={{ backgroundColor: '#8B4513', color: 'white' }}
                            >
                                הוסף
                            </Button>
                        </Stack>
                    </DialogContent>
                </Dialog>
                {/* Trainee Selection Modal */}
                <Dialog open={isTraineeModalOpen} onClose={() => setIsTraineeModalOpen(false)}>
                    <DialogTitle>בחר מתאמן לשמירת התוכנית</DialogTitle>
                    <DialogContent>
                        {trainees.length === 0 ? (
                            <Typography>אין מתאמנים זמינים.</Typography>
                        ) : (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2 }}>
                                {trainees.map(trainee => (
                                    <Box
                                        key={trainee._id}
                                        onClick={() => {
                                            setSelectedTrainee(trainee);
                                            setIsTraineeModalOpen(false);
                                            setIsConfirmModalOpen(true);
                                        }}
                                        sx={{ cursor: 'pointer', textAlign: 'center' }}
                                    >
                                        <img
                                            src={trainee.profileImageBase64}
                                            alt={trainee.username}
                                            style={{ width: '100px', height: '100px', borderRadius: '50%' }}
                                        />
                                        <Typography>{trainee.username}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Confirmation Modal */}
                <Dialog open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
                    <DialogTitle fontFamily="GveretLevin" textAlign="right" direction="rtl">אישור שמירה</DialogTitle>
                    <DialogContent>
                        <Typography textAlign="right" direction="rtl">:האם ברצונך להוסיף את התוכנית ל</Typography>
                        <Typography textAlign="right" direction="rtl">{selectedTrainee?.username}?</Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button onClick={handleConfirmSave}>כן</Button>
                            <Button onClick={() => setIsConfirmModalOpen(false)}>לא</Button>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Save Success Popup */}
                {showPopup && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        onClick={() => setShowPopup(false)}
                    >
                        <div
                            style={{
                                backgroundColor: '#FFF',
                                padding: '40px',
                                borderRadius: '10px',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                maxWidth: '400px',
                                width: '70%'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <span style={{ fontSize: '48px' }}>👍</span>
                            </div>
                            <p style={{ fontSize: '24px', fontWeight: '700', textAlign: 'center', marginBottom: '20px' }}>
                                !התוכנית נשמרה
                            </p>
                            <button
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#B5A78A',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onClick={() => setShowPopup(false)}
                            >
                                <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700', marginRight: '5px' }}>!</span>
                                <MonetizationOnIcon sx={{ fontSize: '20px', color: '#FFD700' }} />
                                <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700', marginRight: '5px' }}>5 קיבלת</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Save Button */}
                <Button
                    onClick={() => setIsTraineeModalOpen(true)}
                    disabled={
                        (selectedProgramType === 'prebuilt' && Object.keys(preDefinedProgram).length === 0) ||
                        (selectedProgramType === 'custom' && Object.keys(customProgram).length === 0)
                    }
                >
                    שמירה
                </Button>
            </Stack>
        </Box >
    );
};

export default Workout;